import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Search, Download, FileText, MessageSquare } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../services/adminApi';
import { Pagination } from '../components/Pagination';
import { getLanguageDisplay } from '../../constants/languages';
import { COLORS } from '../../theme/colors';

function formatDuration(createdAt, updatedAt) {
  if (!createdAt || !updatedAt) return '-';
  const diffMs = new Date(updatedAt) - new Date(createdAt);
  if (diffMs < 1000) return '-';
  const totalSec = Math.round(diffMs / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}m ${sec}s`;
}

const LIMIT = 30;

const STATUS_BADGES = {
  uploaded: 'admin-badge-info',
  processing: 'admin-badge-warning',
  completed: 'admin-badge-success',
  failed: 'admin-badge-danger'
};

export function DocumentsPage() {
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getDocuments({
        search: search || undefined,
        status: statusFilter || undefined,
        skip: (page - 1) * LIMIT,
        limit: LIMIT
      });
      setDocuments(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadDocuments();
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete document "${doc.original_filename}"?`)) return;
    try {
      await adminApi.deleteDocument(doc.id);
      loadDocuments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleDownload = async (docId, fileType) => {
    try {
      const response = await adminApi.downloadDocument(docId, fileType);
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*=UTF-8''(.+)/);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : `document.docx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download file');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Documents</h1>

      <form onSubmit={handleSearch} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)' }} />
            <input
              type="text"
              className="admin-input"
              placeholder="Search by filename..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 40 }}
            />
          </div>
          <select
            className="admin-input"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: 150 }}
          >
            <option value="">All Status</option>
            <option value="uploaded">Uploaded</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <button type="submit" className="admin-btn admin-btn-secondary">Search</button>
        </div>
      </form>

      <div className="admin-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Filename</th>
              <th>Languages</th>
              <th>Status</th>
              <th>Doc Chars</th>
              <th>Tokens</th>
              <th>Cost</th>
              <th>User</th>
              <th>Duration</th>
              <th>Created</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 40 }}>Loading...</td>
              </tr>
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No documents found</td>
              </tr>
            ) : (
              documents.map(doc => (
                <tr key={doc.id}>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.original_filename}
                  </td>
                  <td>
                    <span style={{ fontSize: 12 }}>
                      {getLanguageDisplay(doc.language_from)} → {getLanguageDisplay(doc.language_to)}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-badge ${STATUS_BADGES[doc.status] || ''}`}>
                      {doc.status}
                    </span>
                  </td>
                  <td>
                    {doc.metadata?.char_count?.toLocaleString() || '-'}
                  </td>
                  <td>
                    {doc.metadata?.total_tokens_used?.toLocaleString() || '-'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {(() => {
                      const inp = doc.metadata?.input_tokens;
                      const out = doc.metadata?.output_tokens;
                      if (!inp && !out) return '-';
                      const cost = ((inp || 0) * 3 + (out || 0) * 15) / 1_000_000;
                      return '$' + cost.toFixed(3);
                    })()}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {doc.user_email ? (
                      <Link to={`/admin/users?search=${encodeURIComponent(doc.user_email)}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                        {doc.user_email}
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>{doc.user_id}</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: doc.status === 'completed' ? 'var(--success-hover)' : 'var(--text-muted)' }}>
                    {doc.status === 'completed' ? formatDuration(doc.created_at, doc.updated_at) : '-'}
                  </td>
                  <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {doc.chat_id && (
                        <Link
                          to={`/admin/chats?search=${encodeURIComponent(doc.original_filename)}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
                          title="View chat"
                        >
                          <MessageSquare size={16} color={COLORS.textMuted} />
                        </Link>
                      )}
                      {doc.minio_original_key && (
                        <button
                          onClick={() => handleDownload(doc.id, 'original')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Download original"
                        >
                          <FileText size={16} color={COLORS.textMuted} />
                        </button>
                      )}
                      {doc.minio_translated_key && (
                        <button
                          onClick={() => handleDownload(doc.id, 'translated')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Download translated"
                        >
                          <Download size={16} color={COLORS.accent} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(doc)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        title="Delete"
                      >
                        <Trash2 size={16} color={COLORS.danger} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
